/**
 * NowPanel — the body of an object page's Now tab, the same on the
 * estimate, the order and the purchase order
 * (docs/features/journey-visual-language/01_proposal.md §4).
 *
 * Every row is one line in one pattern:
 * - attention: "▲ Problem · object · consequence · time", one fixing step;
 * - to do: "Verb · object · time", one button;
 * - waiting: "Party: what they owe · object · time".
 * The object is a chip (the line, then its document); the time is always
 * shown; the why goes behind an ⓘ, never in the row; one primary button,
 * the rest in the row's ⋯. Rows run attention, to do, waiting, at most three
 * of each before "+N more". With nothing yours to do it says so, then lists
 * what it is waiting on; a finished object shows its closing line. An
 * optional footer closes the card (the estimate's job row).
 *
 * The rows' words come from the backend; this only lays them out.
 */
import { useState, type ReactNode } from "react";
import { MoreHorizontal, TriangleAlert } from "lucide-react";
import { Button } from "../primitives/button";
import { Card, CardContent } from "../primitives/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";
import { cn } from "../primitives/utils";
import { InfoTip } from "./InfoTip";

export type NowTone = "do" | "waiting" | "attention" | "done";

export interface NowPanelItem {
  key: string;
  tone: NowTone;
  /**
   * To do: a verb first ("Approve artwork for print"). Waiting: the party
   * first ("Acme Ltd: approve proof 1"). Attention: the problem. Done: the
   * closing line.
   */
  lead: string;
  /** The object, as chips: the line, then its document. */
  objects?: ReactNode;
  /** Attention: what the problem costs. */
  consequence?: string | null;
  /** The time, in the rail's date style: "sent 2 Oct", "exp 6 Oct". */
  when?: string | null;
  /** Why, behind an ⓘ. */
  info?: string | null;
  /** The one primary button. */
  action?: ReactNode;
  /** The row's other steps: DropdownMenuItems for its ⋯. */
  more?: ReactNode;
  isLate?: boolean;
}

/** More than this in a group, and the rest wait behind "+N more". */
const MAX_PER_GROUP = 3;

function Dot({ children }: { children: ReactNode }) {
  return (
    <>
      <span aria-hidden className="text-muted-foreground">
        ·
      </span>
      {children}
    </>
  );
}

function Row({ item }: { item: NowPanelItem }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 py-2 first:pt-0 last:pb-0">
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {item.tone === "attention" && (
          <>
            <TriangleAlert
              aria-hidden
              className={cn(
                "size-4 shrink-0",
                item.isLate ? "text-red-600" : "text-amber-600",
              )}
            />
            <span className="sr-only">Needs attention: </span>
          </>
        )}
        {item.tone === "do" && (
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full bg-primary"
          />
        )}
        <span className="font-medium text-foreground">{item.lead}</span>
        {item.objects && <Dot>{item.objects}</Dot>}
        {item.consequence && (
          <Dot>
            <span className="text-foreground">{item.consequence}</span>
          </Dot>
        )}
        {item.when && (
          <Dot>
            <span
              className={cn(
                "tabular-nums",
                item.isLate ? "text-red-700" : "text-muted-foreground",
              )}
            >
              {item.when}
            </span>
          </Dot>
        )}
        {item.info && (
          <InfoTip label={`About: ${item.lead}`}>{item.info}</InfoTip>
        )}
      </span>
      {(item.action || item.more) && (
        <span className="inline-flex items-center gap-1">
          {item.action}
          {item.more && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 px-0"
                  aria-label={`More for: ${item.lead}`}
                >
                  <MoreHorizontal aria-hidden className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">{item.more}</DropdownMenuContent>
            </DropdownMenu>
          )}
        </span>
      )}
    </li>
  );
}

/** A group's rows: three, then "+N more" to show the rest. */
function Rows({ items }: { items: NowPanelItem[] }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? items : items.slice(0, MAX_PER_GROUP);
  const hidden = items.length - shown.length;
  return (
    <>
      {shown.map((item) => (
        <Row key={item.key} item={item} />
      ))}
      {hidden > 0 && (
        <li className="pt-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-muted-foreground"
            onClick={() => setShowAll(true)}
          >
            +{hidden} more
          </Button>
        </li>
      )}
    </>
  );
}

function List({ title, groups }: { title: string; groups: NowPanelItem[][] }) {
  return (
    <section aria-label={title} className="space-y-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      <ul className="divide-y divide-border text-sm">
        {groups
          .filter((g) => g.length > 0)
          .map((g) => (
            <Rows key={g[0]!.key} items={g} />
          ))}
      </ul>
    </section>
  );
}

export function NowPanel({
  items,
  footer,
}: {
  items: NowPanelItem[];
  /** One more section at the card's foot: what the object became, say. */
  footer?: ReactNode;
}) {
  const attention = items.filter((i) => i.tone === "attention");
  const toDo = items.filter((i) => i.tone === "do");
  const waiting = items.filter((i) => i.tone === "waiting");
  const done = items.find((i) => i.tone === "done");
  const yours = attention.length + toDo.length > 0;
  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        {yours ? (
          <List title="What you need to do" groups={[attention, toDo]} />
        ) : waiting.length > 0 || !done ? (
          <p className="text-sm font-medium">Nothing for you right now.</p>
        ) : (
          <ul className="text-sm">
            <Row item={done} />
          </ul>
        )}
        {waiting.length > 0 && <List title="Waiting on" groups={[waiting]} />}
        {footer && <div className="border-t border-border pt-4">{footer}</div>}
      </CardContent>
    </Card>
  );
}
